import React, { useState, useEffect } from 'react';
import './ProcessingCard.css';

/**
 * ProcessingCard — Phantom/placeholder card shown when a study tool generation
 * is in-progress or has failed.  Matches the visual style of the host panel's
 * history cards but is non-interactive.
 *
 * Props:
 *   job        – job object from useGenerationJobs (id, toolType, status, title, metadata, startedAt, error)
 *   onDismiss  – callback(jobId) to remove a failed card
 *   onRetry    – optional callback(job) to retry a failed generation
 */

const TOOL_ICONS = {
  quiz: '🧠',
  flashcards: '🏷️',
  podcast: '🎙️',
  audiobook: '📖',
};

const TOOL_LABELS = {
  quiz: 'Quiz',
  flashcards: 'Flashcard Deck',
  podcast: 'Podcast',
  audiobook: 'Audiobook',
};

function formatElapsed(isoString) {
  if (!isoString) return '';
  const ms = Date.now() - new Date(isoString).getTime();
  if (ms < 0) return 'just now';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

const ProcessingCard = ({ job, onDismiss, onRetry }) => {
  // Re-render every 30s so the elapsed timestamp stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!job || job.status !== 'processing') return;
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, [job]);

  if (!job) return null;

  const isProcessing = job.status === 'processing';
  const isFailed = job.status === 'failed';
  const icon = TOOL_ICONS[job.toolType] || '⏳';
  const label = TOOL_LABELS[job.toolType] || 'Item';
  const meta = job.metadata || {};

  return (
    <div className={`gen-card gen-card--${job.status}`} aria-disabled="true">
      {/* Pulse overlay for processing state */}
      {isProcessing && <div className="gen-card__pulse" />}

      <div className="gen-card__body">
        {/* Top row: icon + title */}
        <div className="gen-card__top">
          <span className="gen-card__icon">{icon}</span>
          <span className="gen-card__title">
            {job.title || `Generating ${label}…`}
          </span>
        </div>

        {/* Status line */}
        <div className="gen-card__status">
          {isProcessing && (
            <>
              <span className="gen-card__spinner" />
              <span>Generating {label.toLowerCase()}…</span>
            </>
          )}
          {isFailed && (
            <>
              <span className="gen-card__fail-icon">✕</span>
              <span>{job.error || 'Generation failed'}</span>
            </>
          )}
        </div>

        {/* Metadata chips */}
        <div className="gen-card__meta">
          {meta.documentCount > 0 && (
            <span className="gen-card__chip">📄 {meta.documentCount} doc{meta.documentCount !== 1 ? 's' : ''}</span>
          )}
          {meta.numQuestions > 0 && (
            <span className="gen-card__chip">📝 {meta.numQuestions} questions</span>
          )}
          {meta.numCards > 0 && (
            <span className="gen-card__chip">🃏 {meta.numCards} cards</span>
          )}
          {meta.difficultyLevel && (
            <span className="gen-card__chip">🎯 {meta.difficultyLevel}</span>
          )}
          <span className="gen-card__chip">⏱️ {formatElapsed(job.startedAt)}</span>
        </div>

        {/* Helper text */}
        {isProcessing && (
          <div className="gen-card__helper">
            Will appear in your history when ready
          </div>
        )}

        {/* Failed actions */}
        {isFailed && (
          <div className="gen-card__actions">
            {onRetry && (
              <button className="gen-card__btn gen-card__btn--retry" onClick={() => onRetry(job)}>
                ↻ Retry
              </button>
            )}
            {onDismiss && (
              <button className="gen-card__btn gen-card__btn--dismiss" onClick={() => onDismiss(job.id)}>
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingCard;
